import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";

const Stat = ({ label, value, note }) => (
  <div className="bg-white border border-line px-6 py-6">
    <div className="text-[9.5px] tracking-[0.2em] uppercase text-[#736F68]">{label}</div>
    <div className="display text-[34px] leading-none text-charcoal mt-3">
      {value === null || value === undefined ? "—" : value}
    </div>
    {note && <div className="text-[11.5px] font-light text-[#6E6A63] mt-2.5">{note}</div>}
  </div>
);

const FunnelRow = ({ label, value, max }) => (
  <div className="py-4 border-b border-line" data-testid={`funnel-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-baseline justify-between mb-2">
      <span className="text-[13.5px] text-charcoal">{label}</span>
      <span className="display text-[20px] text-charcoal">{value ?? "—"}</span>
    </div>
    <div className="h-[3px] bg-line">
      <div
        className="h-full bg-sage transition-all duration-700"
        style={{ width: max ? `${Math.min(100, ((value || 0) / max) * 100)}%` : "0%" }}
      />
    </div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <AdminPage title="Dashboard" />;
  const f = d.funnel;
  const max = Math.max(f.visitors, 1);

  return (
    <AdminPage
      title="Revenue dashboard"
      lede="Visitor → tool → CTA → click funnel from the event architecture. Revenue and conversion values stay empty until a merchant or network actually reports them."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line mb-10">
        <Stat label="Articles" value={d.counts.articles} note={`${d.counts.published} published`} />
        <Stat label="Affiliate offers" value={d.counts.offers} note={`${d.counts.active_offers} active`} />
        <Stat label="Subscribers" value={d.counts.subscribers} note="Stored locally, MailerLite-ready" />
        <Stat label="Affiliate CTR" value={d.rates.cta_ctr !== null ? `${d.rates.cta_ctr}%` : "—"} note="Clicks / impressions" />
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8">
        <div className="bg-white border border-line px-6 md:px-8 py-7" data-testid="funnel-panel">
          <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">Commercial funnel</div>
          <FunnelRow label="Visitors" value={f.visitors} max={max} />
          <FunnelRow label="Tool starts" value={f.tool_starts} max={max} />
          <FunnelRow label="Tool completions" value={f.tool_completions} max={max} />
          <FunnelRow label="Affiliate CTA impressions" value={f.cta_impressions} max={max} />
          <FunnelRow label="Affiliate clicks" value={f.affiliate_clicks} max={max} />
          <FunnelRow label="Leads / sales when known" value={f.known_leads} max={max} />
          <div className="pt-5">
            <div className="text-[13.5px] text-charcoal">Known revenue</div>
            <div className="display text-[26px] text-charcoal mt-1">Not reported</div>
            <p className="text-[12px] font-light text-[#6E6A63] mt-3 leading-relaxed">{d.revenue_note}</p>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white border border-line px-6 md:px-8 py-7">
            <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">Derived metrics</div>
            {[
              ["Tool completion rate", d.rates.tool_completion_rate !== null ? `${d.rates.tool_completion_rate}%` : "—"],
              ["Revenue per visitor", "—"],
              ["Revenue per 1,000 visitors", "—"],
              ["Revenue per tool completion", "—"],
              ["Average commission", "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between py-3 border-b border-line last:border-0">
                <span className="text-[13.5px] font-light text-[#5A5A5A]">{k}</span>
                <span className="text-[14.5px] text-charcoal">{v}</span>
              </div>
            ))}
          </div>

          <div className="bg-white border border-line px-6 md:px-8 py-7" data-testid="cta-placement-panel">
            <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">By CTA placement</div>
            {d.by_placement.length === 0 && (
              <p className="text-[13px] font-light text-[#6E6A63]">No CTA events recorded yet.</p>
            )}
            {d.by_placement.map((r, i) => (
              <div key={i} className="flex items-baseline justify-between py-2.5 border-b border-line last:border-0">
                <span className="text-[13px] font-light text-[#5A5A5A]">
                  {r.placement || "unattributed"} · {r.type === "cta_click" ? "clicks" : "impressions"}
                </span>
                <span className="text-[14px] text-charcoal">{r.count}</span>
              </div>
            ))}
          </div>

          <div className="bg-white border border-line px-6 md:px-8 py-7" data-testid="tool-events-panel">
            <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">By tool</div>
            {d.by_tool.length === 0 && (
              <p className="text-[13px] font-light text-[#6E6A63]">No tool events recorded yet.</p>
            )}
            {d.by_tool.map((r, i) => (
              <div key={i} className="flex items-baseline justify-between py-2.5 border-b border-line last:border-0">
                <span className="text-[13px] font-light text-[#5A5A5A]">
                  {r.tool} · {r.type.replace("tool_", "")}
                </span>
                <span className="text-[14px] text-charcoal">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-line px-6 md:px-8 py-7 mt-8" data-testid="article-views-panel">
        <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">Most viewed guides</div>
        {d.by_article.length === 0 && (
          <p className="text-[13px] font-light text-[#6E6A63]">No article views recorded yet.</p>
        )}
        {d.by_article.map((r) => (
          <div key={r.slug} className="flex items-baseline justify-between py-2.5 border-b border-line last:border-0">
            <span className="text-[13px] font-light text-[#5A5A5A]">{r.slug}</span>
            <span className="text-[14px] text-charcoal">{r.views}</span>
          </div>
        ))}
      </div>
    </AdminPage>
  );
}
